import React, { useEffect, useRef, useState } from "react";

let _cssLoaded = false;
function ensureLeafletCss() {
  if (_cssLoaded) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css";
  document.head.appendChild(link);
  _cssLoaded = true;
}

/**
 * Interactive map picker — click on map to set marker.
 * Props: lat, lng, onChange({lat,lng})
 */
export default function LocationPicker({ lat, lng, onChange, height = 280 }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);

  const initialLat = lat ?? 42.6977;
  const initialLng = lng ?? 23.3219; // Sofia default

  useEffect(() => {
    ensureLeafletCss();
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !ref.current) return;
      if (!mapRef.current) {
        mapRef.current = L.map(ref.current, {
          zoomControl: true,
          attributionControl: false,
        }).setView([initialLat, initialLng], lat ? 14 : 11);
        L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
          maxZoom: 19,
        }).addTo(mapRef.current);

        const icon = L.divIcon({
          className: "",
          html: `<div style="width:16px;height:16px;background:#3B82F6;border:2px solid #000;box-shadow:0 0 14px #3B82F6;border-radius:50%"></div>`,
        });
        if (lat != null && lng != null) {
          markerRef.current = L.marker([lat, lng], { icon }).addTo(mapRef.current);
        }
        mapRef.current.on("click", (e) => {
          const { lat: la, lng: ln } = e.latlng;
          if (markerRef.current) markerRef.current.setLatLng([la, ln]);
          else markerRef.current = L.marker([la, ln], { icon }).addTo(mapRef.current);
          onChange?.({ lat: la, lng: ln });
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, []);

  // Update marker when lat/lng change externally
  useEffect(() => {
    (async () => {
      if (lat == null || lng == null || !mapRef.current) return;
      const L = (await import("leaflet")).default;
      if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
      else {
        const icon = L.divIcon({
          className: "",
          html: `<div style="width:16px;height:16px;background:#3B82F6;border:2px solid #000;box-shadow:0 0 14px #3B82F6;border-radius:50%"></div>`,
        });
        markerRef.current = L.marker([lat, lng], { icon }).addTo(mapRef.current);
      }
      mapRef.current.panTo([lat, lng]);
    })();
  }, [lat, lng]);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapRef = useRef(null);

  // Live autocomplete via Nominatim
  useEffect(() => {
    const q = (query || "").trim();
    if (q.length < 2) { setSuggestions([]); return; }
    const ctrl = new AbortController();
    const tid = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=8&addressdetails=1&countrycodes=bg&q=${encodeURIComponent(q)}`,
          { signal: ctrl.signal, headers: { Accept: "application/json" } }
        );
        const data = await res.json();
        setSuggestions((data || []).map((x) => {
          const addr = x.address || {};
          const short = addr.neighbourhood || addr.suburb || addr.quarter || addr.city_district || addr.city || addr.town || addr.village || x.display_name?.split(",")[0];
          return { id: x.place_id, name: x.display_name, short, lat: parseFloat(x.lat), lng: parseFloat(x.lon) };
        }));
      } catch (_) {}
      setSearching(false);
    }, 350);
    return () => { clearTimeout(tid); ctrl.abort(); };
  }, [query]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (s) => {
    onChange?.({ lat: s.lat, lng: s.lng, address: s.name });
    setQuery(s.short);
    setShowSuggestions(false);
    if (mapRef.current) mapRef.current.setView([s.lat, s.lng], 14);
  };

  const search = async () => {
    if (!query.trim()) return;
    if (suggestions[0]) pick(suggestions[0]);
  };

  return (
    <div className="space-y-2" data-testid="location-picker">
      <div className="relative" ref={wrapRef}>
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); search(); }
          }}
          placeholder="Search address (e.g. Mladost 1, Sofia)..."
          className="w-full bg-[#111D33] border border-white/10 pl-3 pr-10 py-2.5 text-sm focus:border-pitch outline-none"
          data-testid="loc-search-input"
        />
        {searching && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-pitch/70">...</span>
        )}
        {showSuggestions && query.length >= 2 && suggestions.length > 0 && (
          <div className="absolute z-30 left-0 right-0 mt-1 glass-elevated max-h-64 overflow-y-auto scroll-thin" data-testid="loc-suggestions">
            {suggestions.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => pick(s)}
                className="w-full text-left p-3 hover:bg-white/5 border-b border-white/5 last:border-0 transition-colors"
                data-testid={`loc-suggestion-${s.id}`}
              >
                <div className="font-oswald uppercase tracking-tight text-sm">{s.short}</div>
                <div className="text-[10px] font-mono text-white/35 mt-0.5 line-clamp-1">{s.name}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div ref={ref} style={{ height, width: "100%" }} className="border border-white/10 rounded-md overflow-hidden" />
      {lat != null && lng != null && (
        <div className="label-mono text-pitch">
          {lat.toFixed(5)}, {lng.toFixed(5)}
        </div>
      )}
    </div>
  );
}
